import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, PrinterIcon, XIcon, AtSymbolIcon, CheckIcon, PlusIcon } from '@heroicons/react/solid';
import { GET_COMPANY_USERS } from 'data/company';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';
import { CompanyUser } from 'components/UsersList/UsersList';
import Avatar from 'components/Avatar';
import { classNames } from 'utils';
import button from 'styles/button';
import {
  ExportFormat,
  ExportMethod,
  ExportTimeRangeOptions,
  ExportTypes,
  ScoreBoardExportSections,
} from 'types/export';
import Loader from 'components/Loader';
import isEmail from 'utils/validateEmail';
import { useReactToPrint } from 'react-to-print';
import DataExtractor from 'utils/dataExtractor';
import React from 'react';
import { useSession } from 'next-auth/react';
import { serverClient } from 'src/urqlClient';
import { Action } from 'data/actions/types';
import { Objective } from 'data/objectives/types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ErrorToast, SuccessToast } from 'components/Toast';
import { ErrorToastError } from 'components/Toast/ErrorToast';

const resolveHeader = (exportType: ExportTypes) => {
  switch (exportType) {
    case ExportTypes.Actions:
      return [
        { id: 'id', title: 'Id' },
        { id: 'title', title: 'Title' },
        { id: 'date', title: 'Date' },
        { id: 'assignee', title: 'Assignee' },
        { id: 'description', title: 'Description' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'comments', title: 'Comments' },
      ];
    case ExportTypes.Objectives:
      return [
        { id: 'id', title: 'Id' },
        { id: 'title', title: 'Title' },
        { id: 'status', title: 'status' },
        { id: 'due', title: 'Due Date' },
        { id: 'owned_user', title: 'Owner' },
        { id: 'description', title: 'Description' },
        { id: 'milestone30days', title: '30 Days Milestones' },
        { id: 'milestone60days', title: '60 Days Milestones' },
        { id: 'keyResults', title: 'Key Results' },
      ];
    default:
      return null;
  }
};

interface ExportPopupProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  type: ExportTypes;
  setSuccess?: (message: string) => void;
}

interface PrintComponentProps<T extends Action[] | Objective[]> {
  data: T;
  type: ExportTypes;
}

const PrintComponent = React.forwardRef<HTMLDivElement, PrintComponentProps<Action[] | Objective[]>>((props, ref) => {
  const headerValues: { id: string; title: string }[] = resolveHeader(props.type);
  const getRowBackgroundColor = (index) => {
    return index % 2 === 0 ? '#E5E4E2' : '#F5F5F5'; // Two shades of light blue
  };

  return (
    <div ref={ref} style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h2 style={{ textAlign: 'center' }}>Exported Data</h2>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px',
        }}
      >
        <thead
          style={{
            border: '1px solid black',
            padding: '8px',
            textAlign: 'center',
          }}
        >
          <tr>
            {headerValues.map((header, index) => (
              <th key={index}>{header.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.data.map((record, recordIndex) => (
            <tr key={recordIndex} style={{ backgroundColor: getRowBackgroundColor(recordIndex) }}>
              {headerValues.map((header, headerIndex) => (
                <td
                  style={{
                    border: '1px solid black', // Add borders to td elements
                    padding: '8px', // Padding for table cells
                    textAlign: 'left', // Align text to left; adjust as needed
                  }}
                  key={headerIndex}
                >
                  {record[header.id]}
                </td> // Dynamically access the property
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
PrintComponent.displayName = 'PrintComponent';

const ExportPopup: React.FC<ExportPopupProps> = ({ setIsOpen, isOpen, type, setSuccess }) => {
  const cancelButtonRef = useRef(null);
  const router = useRouter();

  const [pageType] = useState(type);
  const [exportMethod, setExportMethod] = useState<ExportMethod>(ExportMethod.Email);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [emailRecipients, setEmailRecipients] = useState<CompanyUser[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | undefined>(ExportFormat.CSV);
  const [selectedTimeRange, setSelectedTimeRange] = useState<ExportTimeRangeOptions | undefined>();
  const [showTimeRangeError, setShowTimeRangeError] = useState<boolean>(false);
  const [showFormatError, setShowFormatError] = useState<boolean>(false);
  const [showRecipientsError, setShowRecipientsError] = useState<boolean>(false);
  const [showCustomEmailDialog, setShowCustomEmailDialog] = useState<boolean>(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [customEmail, setCustomEmail] = useState<string | undefined>();
  const [emailSent, setEmailSent] = useState(false);
  const [customEmailRecipients, setCustomemailRecipients] = useState<string[]>([]);
  const [showEmailError, setshowEmailerror] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedScoreboardSections, setSelectedScoreboardSections] = useState<ScoreBoardExportSections[]>([]);
  const [preparingPrint, setPreparingPrint] = useState<boolean>(false);
  const [printInitiated, setPrintInitiated] = useState(false);
  const [error, setError] = useState<ErrorToastError>();
  const { data: session } = useSession();
  const companyId = router.query.company;
  const teamId = router.query.team;

  const [usersResult] = useQuery({
    query: GET_COMPANY_USERS,
    variables: { company_id: companyId },
  });

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => resetPrintState(),
  });

  const fetchDataAndPrint = async () => {
    setShowTimeRangeError(false);
    setShowFormatError(false);
    setShowRecipientsError(false);

    // Validate selections
    if (!selectedTimeRange) {
      setShowTimeRangeError(true);
    }
    if (!selectedFormat) {
      setShowFormatError(true);
    }

    if (selectedTimeRange && selectedFormat) {
      setPreparingPrint(true);
      const client = serverClient(session?.token as unknown as string);
      const extractor = DataExtractor.getInstance(client);
      const customTimeRangeOptions = {
        startDate,
        endDate,
      };

      const fetchedData = await extractor.extractData(
        selectedTimeRange,
        type,
        Number(teamId),
        Number(companyId),
        customTimeRangeOptions,
      );

      if (Array.isArray(fetchedData) && fetchedData.length === 0) {
        setError({
          message: 'No records found for the specified duration',
          name: 'No Content',
        });
        setPreparingPrint(false);
      } else {
        setData(fetchedData);
        setPreparingPrint(false);
      }
    }
  };

  useEffect(() => {
    if (data && data.length > 0 && !printInitiated) {
      handlePrint();
      setPrintInitiated(true); // Ensure print is only initiated once
    }
  }, [data, handlePrint, printInitiated]);

  const resetPrintState = () => {
    setData([]);
    setPrintInitiated(false);
  };

  useEffect(() => {
    return () => {
      // This cleanup function runs when the component unmounts/closed
      resetPrintState();
    };
  }, []);

  useEffect(() => {
    if (!usersResult.fetching && !usersResult.error && usersResult.data?.company_users) {
      setCompanyUsers(usersResult.data.company_users);
    }
  }, [usersResult]);

  // Updated toggleEmailRecipients function
  const toggleEmailRecipients = (userOrUserId) => {
    if (typeof userOrUserId === 'object') {
      // Assuming userOrUserId is a user object being removed
      setEmailRecipients((currentRecipients) => currentRecipients.filter((user) => user.id !== userOrUserId.id));
      setCompanyUsers((currentUsers) => [...currentUsers, userOrUserId]);
    } else {
      // Assuming userOrUserId is a userId being added
      setShowRecipientsError(false);
      const userToAdd = companyUsers.find((user) => user.id === userOrUserId);
      if (userToAdd) {
        setEmailRecipients((currentRecipients) => [...currentRecipients, userToAdd]);
        setCompanyUsers((currentUsers) => currentUsers.filter((user) => user.id !== userOrUserId));
      }
    }
  };

  // Handler for changing the time range selection
  const handleTimeRangeChange = (event) => {
    const { value } = event.target;
    setShowTimeRangeError(false);
    if (value === 'Custom') {
      // Handle custom range logic here
      setSelectedTimeRange(value);
      // Optionally reset date pickers here
    } else {
      setSelectedTimeRange(value);
    }
  };

  // Handler for multiple selection of time ranges in scoreboard page
  const handleSectionSelectionScoreboard = (event) => {
    const { value } = event.target;
    const scoreboardSections = [...selectedScoreboardSections];
    setShowTimeRangeError(false);
    if (!selectedScoreboardSections.includes(value)) {
      setSelectedScoreboardSections([...scoreboardSections, value]);
    } else {
      const i = scoreboardSections.indexOf(value);
      scoreboardSections.splice(i, 1);
      setSelectedScoreboardSections(scoreboardSections);
    }
  };

  // Handler for changing the format selection
  const handleFormatSelection = (event) => {
    const { value } = event.target;
    setShowFormatError(false);
    setSelectedFormat(value);
  };

  const handleCustomUserEmailSubmit = (email) => {
    if (!isEmail(email)) {
      setshowEmailerror(true);
    } else {
      setshowEmailerror(false);
      const updatedCustomEmails = [...customEmailRecipients, email];
      setCustomemailRecipients(updatedCustomEmails);
      setCustomEmail('');
      setShowCustomEmailDialog(false);
    }
  };

  const handleCustomEmailRemoval = (recipient) => {
    const updatedCustomEmails = customEmailRecipients.filter((el) => el !== recipient);
    setCustomemailRecipients(updatedCustomEmails);
  };

  const handleExport = async () => {
    // Reset error states
    setShowTimeRangeError(false);
    setShowFormatError(false);
    setShowRecipientsError(false);

    // Validate selections
    if (!selectedTimeRange) {
      setShowTimeRangeError(true);
    }
    if (!selectedFormat) {
      setShowFormatError(true);
    }
    if (emailRecipients.length === 0 && customEmailRecipients.length === 0) {
      setShowRecipientsError(true);
    }

    // Proceed with export if all selections are valid
    if (selectedTimeRange && selectedFormat && (emailRecipients.length > 0 || customEmailRecipients.length > 0)) {
      setIsEmailSending(true); // Email sending starts
      const response = await fetch(`/api/email/export-data`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          companyUsers,
          emailRecipients: [...emailRecipients.map((e) => e.user.email), ...customEmailRecipients],
          selectedFormat,
          selectedTimeRange,
          exportMethod,
          exportType: pageType,
          companyId,
          teamId,
          customTimeRange: {
            startDate,
            endDate,
          },
          sections: selectedScoreboardSections,
        }),
      });
      if (response.status == 200) {
        setIsEmailSending(false);
        setEmailSent(true);
        setTimeout(() => {
          setEmailSent(false);
        }, 1500);
        setSuccess('Export Successful.');
        setIsOpen(false);
      } else if (response.status == 204) {
        setIsEmailSending(false);
        setError({
          message: 'No records found for the specified duration',
          name: 'No Content',
        });
      } else {
        setIsEmailSending(false);
        setError({
          message: 'There was an error sending the Email.',
          name: 'Error',
        });
      }
    }
  };

  return (
    <>
      <Transition.Root show={isOpen}>
        <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg z-10">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left font-Poppins">
                        <Dialog.Title as="h3" className="text-base leading-10 text-lg font-bold  text-gray-800">
                          Export Content
                        </Dialog.Title>
                        <div className="flex flex-col">
                          {(() => {
                            switch (pageType) {
                              case ExportTypes.Actions:
                                return (
                                  <div className="my-3">
                                    <div className="text-md font-bold">Time Range</div>
                                    <div className="flex items-center mb-1 mt-2">
                                      <input
                                        id="checkbox-last-7-days"
                                        type="checkbox"
                                        value={ExportTimeRangeOptions.LastSevenDays}
                                        checked={selectedTimeRange === ExportTimeRangeOptions.LastSevenDays}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                        onChange={handleTimeRangeChange}
                                      />
                                      <label htmlFor="checkbox-last-7-days" className="ml-2 text-sm text-gray-500">
                                        Last 7 Days
                                      </label>
                                    </div>
                                    <div className="flex items-center mb-1">
                                      <input
                                        id="checkbox-last-30-days"
                                        type="checkbox"
                                        value={ExportTimeRangeOptions.LastThirtyDays}
                                        checked={selectedTimeRange === ExportTimeRangeOptions.LastThirtyDays}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                        onChange={handleTimeRangeChange}
                                      />
                                      <label htmlFor="checkbox-last-30-days" className="ml-2 text-sm text-gray-500">
                                        Last 30 Days
                                      </label>
                                    </div>
                                    {/* <div className="flex items-center mb-1">
                                      <input
                                        id="checkbox-year-to-date"
                                        type="checkbox"
                                        value={ExportTimeRangeOptions.LastYear}
                                        checked={selectedTimeRange === ExportTimeRangeOptions.LastYear}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                        onChange={handleTimeRangeChange}
                                      />
                                      <label htmlFor="checkbox-year-to-date" className="ml-2 text-sm text-gray-500">
                                        Year to Date
                                      </label>
                                    </div> */}
                                    <div className="flex items-center mb-1">
                                      <input
                                        id="checkbox-all-time"
                                        type="checkbox"
                                        value={ExportTimeRangeOptions.All}
                                        checked={selectedTimeRange === ExportTimeRangeOptions.All}
                                        onChange={handleTimeRangeChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                      />
                                      <label htmlFor="checkbox-all-time" className="ml-2 text-sm text-gray-500">
                                        All Time
                                      </label>
                                    </div>
                                    <div className="flex items-center mb-1">
                                      <input
                                        id="checkbox-custom-range"
                                        type="checkbox"
                                        value="Custom"
                                        checked={selectedTimeRange === ExportTimeRangeOptions.Custom}
                                        onChange={handleTimeRangeChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                      />
                                      <label htmlFor="checkbox-custom-range" className="ml-2 text-sm text-gray-500">
                                        Custom Range
                                      </label>
                                    </div>
                                    {selectedTimeRange === 'Custom' && (
                                      <div className="flex justify-between">
                                        <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} />
                                        <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} />
                                      </div>
                                    )}
                                    {showTimeRangeError && (
                                      <p className="text-red-500 text-xs">Please select a time range.</p>
                                    )}
                                  </div>
                                );
                              case ExportTypes.Objectives:
                                return (
                                  <div className="my-3">
                                    <div className="text-md font-bold">Time Range</div>
                                    {/* <div className="mt-2 flex items-center mb-1">
                                      <input
                                        id="checkbox-year-to-date"
                                        type="checkbox"
                                        value={ExportTimeRangeOptions.LastYear}
                                        checked={selectedTimeRange === ExportTimeRangeOptions.LastYear}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                        onChange={handleTimeRangeChange}
                                      />
                                      <label htmlFor="checkbox-year-to-date" className="ml-2 text-sm text-gray-500">
                                        Year to Date
                                      </label>
                                    </div> */}
                                    <div className="flex items-center mb-1">
                                      <input
                                        id="checkbox-all-time"
                                        type="checkbox"
                                        value={ExportTimeRangeOptions.All}
                                        checked={selectedTimeRange === ExportTimeRangeOptions.All}
                                        onChange={handleTimeRangeChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                      />
                                      <label htmlFor="checkbox-all-time" className="ml-2 text-sm text-gray-500">
                                        All Time
                                      </label>
                                    </div>
                                    <div className="flex items-center mb-1">
                                      <input
                                        id="checkbox-custom-range"
                                        type="checkbox"
                                        value="Custom"
                                        checked={selectedTimeRange === ExportTimeRangeOptions.Custom}
                                        onChange={handleTimeRangeChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                      />
                                      <label htmlFor="checkbox-custom-range" className="ml-2 text-sm text-gray-500">
                                        Custom Range
                                      </label>
                                    </div>
                                    {selectedTimeRange === 'Custom' && (
                                      <div className="flex justify-between">
                                        <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} />
                                        <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} />
                                      </div>
                                    )}
                                  </div>
                                );
                              case ExportTypes.Scoreboard:
                                return (
                                  <>
                                    <div className="my-3">
                                      <div className="text-md font-bold">Section</div>
                                      <div className="grid grid-cols-2 mt-2">
                                        <div className="flex items-center mb-1">
                                          <input
                                            id="scoreboard-section-daily"
                                            type="checkbox"
                                            value={ScoreBoardExportSections.Daily}
                                            checked={selectedScoreboardSections.includes(
                                              ScoreBoardExportSections.Daily,
                                            )}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                            onChange={handleSectionSelectionScoreboard}
                                          />
                                          <label
                                            htmlFor="checkbox-scoreboard-section-daily"
                                            className="ml-2 text-sm text-gray-500"
                                          >
                                            {ScoreBoardExportSections.Daily}
                                          </label>
                                        </div>
                                        <div className="flex items-center mb-1">
                                          <input
                                            id="scoreboard-section-weekly"
                                            type="checkbox"
                                            value={ScoreBoardExportSections.Weekly}
                                            checked={selectedScoreboardSections.includes(
                                              ScoreBoardExportSections.Weekly,
                                            )}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                            onChange={handleSectionSelectionScoreboard}
                                          />
                                          <label
                                            htmlFor="checkbox-scoreboard-section-weekly"
                                            className="ml-2 text-sm text-gray-500"
                                          >
                                            {ScoreBoardExportSections.Weekly}
                                          </label>
                                        </div>
                                        <div className="flex items-center mb-1">
                                          <input
                                            id="scoreboard-section-monthly"
                                            type="checkbox"
                                            value={ScoreBoardExportSections.Monthly}
                                            checked={selectedScoreboardSections.includes(
                                              ScoreBoardExportSections.Monthly,
                                            )}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                            onChange={handleSectionSelectionScoreboard}
                                          />
                                          <label
                                            htmlFor="checkbox-scoreboard-section-monthly"
                                            className="ml-2 text-sm text-gray-500"
                                          >
                                            {ScoreBoardExportSections.Monthly}
                                          </label>
                                        </div>
                                        <div className="flex items-center mb-1">
                                          <input
                                            id="scoreboard-section-quarterly"
                                            type="checkbox"
                                            value={ScoreBoardExportSections.Quarterly}
                                            checked={selectedScoreboardSections.includes(
                                              ScoreBoardExportSections.Quarterly,
                                            )}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                            onChange={handleSectionSelectionScoreboard}
                                          />
                                          <label
                                            htmlFor="checkbox-scoreboard-section-quarterly"
                                            className="ml-2 text-sm text-gray-500"
                                          >
                                            {ScoreBoardExportSections.Quarterly}
                                          </label>
                                        </div>
                                        <div className="flex items-center mb-1">
                                          <input
                                            id="scoreboard-section-yearly"
                                            type="checkbox"
                                            value={ScoreBoardExportSections.Annually}
                                            checked={selectedScoreboardSections.includes(
                                              ScoreBoardExportSections.Annually,
                                            )}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                            onChange={handleSectionSelectionScoreboard}
                                          />
                                          <label
                                            htmlFor="checkbox-scoreboard-section-yearly"
                                            className="ml-2 text-sm text-gray-500"
                                          >
                                            {ScoreBoardExportSections.Annually}
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="my-3">
                                      <div className="text-md font-bold">Time Range</div>
                                      <div className="flex items-center mb-1">
                                        <input
                                          id="checkbox-all-time"
                                          type="checkbox"
                                          value={ExportTimeRangeOptions.All}
                                          checked={selectedTimeRange === ExportTimeRangeOptions.All}
                                          onChange={handleTimeRangeChange}
                                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                        />
                                        <label htmlFor="checkbox-all-time" className="ml-2 text-sm text-gray-500">
                                          All Time
                                        </label>
                                      </div>
                                      <div className="flex items-center mb-1">
                                        <input
                                          id="checkbox-custom-range"
                                          type="checkbox"
                                          value="Custom"
                                          checked={selectedTimeRange === ExportTimeRangeOptions.Custom}
                                          onChange={handleTimeRangeChange}
                                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                                        />
                                        <label htmlFor="checkbox-custom-range" className="ml-2 text-sm text-gray-500">
                                          Custom Range
                                        </label>
                                      </div>
                                      {selectedTimeRange === 'Custom' && (
                                        <div className="flex justify-between">
                                          <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} />
                                          <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} />
                                        </div>
                                      )}
                                    </div>
                                  </>
                                );
                              default:
                                return <>Default</>;
                            }
                          })()}
                          <div className="my-3">
                            <div className="text-md font-bold">Format</div>
                            <div className="flex items-center mb-1 mt-2">
                              <input
                                id="checkbox-csv"
                                type="checkbox"
                                value={ExportFormat.CSV}
                                checked={selectedFormat === ExportFormat.CSV}
                                disabled={true}
                                onChange={handleFormatSelection}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                              />
                              <label htmlFor="checkbox-csv" className="ml-2 text-sm text-gray-500">
                                CSV
                              </label>
                            </div>
                            {showFormatError && <p className="text-red-500 text-xs">Please select a valid format.</p>}
                          </div>
                          <div className="my-3 w-full">
                            <div className="text-md font-bold">Method</div>
                            <div className="flex flex-row mt-2 hover:cursor-pointer">
                              <div
                                onClick={() => setExportMethod(ExportMethod.Email)}
                                className={`flex flex-1 border p-2 rounded-md mr-1 cursor-pointer ${
                                  exportMethod === ExportMethod.Email
                                    ? 'bg-blue-100 border-blue-600'
                                    : 'border-gray-600'
                                }`}
                              >
                                <AtSymbolIcon className="w-6 h-6 mr-1" />
                                <div className="text-sm flex justify-center items-center">Email</div>
                              </div>
                              <div
                                onClick={() => setExportMethod(ExportMethod.Print)}
                                className={`flex flex-1 border p-2 rounded-md mr-1 cursor-pointer ${
                                  exportMethod === ExportMethod.Print
                                    ? 'bg-blue-100 border-blue-600'
                                    : 'border-gray-600'
                                }`}
                              >
                                <PrinterIcon className="w-6 h-6 mr-1" />
                                <div className="text-sm flex justify-center items-center">Print</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex w-full invisible">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to export the content with the above settings? This action cannot be
                            undone.
                          </p>
                        </div>
                        {exportMethod === ExportMethod.Email && (
                          <>
                            <div className="-mt-6 w-full">
                              <Menu>
                                <div className="mt-1">
                                  <Menu.Button className="w-full rounded-md px-4 py-2 h-9 text-left cursor-default text-xs inline-flex justify-between items-center border border-gray-300 shadow-sm bg-white font-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-primary-500 ">
                                    Select Team Members to Email
                                    <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
                                  </Menu.Button>
                                </div>
                                <div className="relative z-30 overflow-visible">
                                  <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                  >
                                    <Menu.Items
                                      static
                                      className="max-h-24 mt-1 text-xs w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none overflow-y-scroll items-center flex-col justify-center"
                                    >
                                      {companyUsers?.map((company_user) => (
                                        <Menu.Item key={company_user.id}>
                                          {({ active }) => (
                                            <div
                                              className={classNames(
                                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                'cursor-pointer select-none relative py-2 pl-3 pr-9 ',
                                              )}
                                              onClick={() => toggleEmailRecipients(company_user.id)}
                                            >
                                              <div className="flex items-center">
                                                <Avatar size="m" user={company_user.user} showName={true} />
                                              </div>
                                            </div>
                                          )}
                                        </Menu.Item>
                                      ))}
                                    </Menu.Items>
                                  </Transition>
                                </div>
                              </Menu>
                            </div>
                            <div className="w-full flex mt-4 flex-wrap -m-2 overflow-y-auto max-h-24">
                              {emailRecipients.map((recipient) => {
                                return (
                                  <div
                                    key={recipient.id}
                                    className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                  >
                                    <Avatar size="s" user={recipient.user} showName={true} />
                                    <div className="cursor-pointer" onClick={() => toggleEmailRecipients(recipient)}>
                                      <XIcon height="16" width="16" />
                                    </div>
                                  </div>
                                );
                              })}
                              {customEmailRecipients.map((recipient) => {
                                return (
                                  <div
                                    key={recipient}
                                    className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                  >
                                    <div className="flex flex-shrink-0 flex-grow-0 items-center justify-center text-base">
                                      {recipient}
                                    </div>
                                    <div className="cursor-pointer" onClick={() => handleCustomEmailRemoval(recipient)}>
                                      <XIcon height="16" width="16" />
                                    </div>
                                  </div>
                                );
                              })}

                              <button
                                type="button"
                                className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                onClick={() => {
                                  setShowCustomEmailDialog(true);
                                }}
                              >
                                <PlusIcon height="16" width="16" />
                                <span className="ml-1">Add Email</span>
                              </button>
                            </div>
                            {showRecipientsError && (
                              <p className="text-red-500 text-xs mt-2">Please add valid email recipients.</p>
                            )}

                            {showCustomEmailDialog && (
                              <>
                                <Transition appear show={showCustomEmailDialog} as={Fragment}>
                                  <Dialog
                                    as="div"
                                    className="relative z-10"
                                    onClose={() => setShowCustomEmailDialog(false)}
                                  >
                                    <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
                                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                                    <Transition.Child
                                      as={Fragment}
                                      enter="ease-out duration-300"
                                      enterFrom="opacity-0"
                                      enterTo="opacity-100"
                                      leave="ease-in duration-200"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <div className="fixed inset-0 bg-black/25" />
                                    </Transition.Child>

                                    <div className="fixed inset-0 overflow-y-auto">
                                      <div className="flex min-h-full items-center justify-center p-4 text-center">
                                        <Transition.Child
                                          as={Fragment}
                                          enter="ease-out duration-300"
                                          enterFrom="opacity-0 scale-95"
                                          enterTo="opacity-100 scale-100"
                                          leave="ease-in duration-200"
                                          leaveFrom="opacity-100 scale-100"
                                          leaveTo="opacity-0 scale-95"
                                        >
                                          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                                            <Dialog.Title
                                              as="h3"
                                              className="text-base leading-10 text-lg font-bold  text-gray-800"
                                            >
                                              Add Email Address
                                            </Dialog.Title>
                                            <label htmlFor="title" className="block text-xs text-gray-700">
                                              Add an email address outside of your team to export the content to.
                                            </label>

                                            <label htmlFor="title" className="block text-xs text-gray-700 mt-4">
                                              Email Address
                                            </label>
                                            <div className="mt-1">
                                              <input
                                                type="text"
                                                name="email"
                                                id="email"
                                                value={customEmail}
                                                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-xs border-gray-300 rounded-md"
                                                placeholder={`Email Address`}
                                                onChange={(event) => setCustomEmail(event.target.value)}
                                              />
                                              {showEmailError && (
                                                <p className="text-red-500 text-xs mt-1">
                                                  Please use a valid email Address.
                                                </p>
                                              )}
                                            </div>
                                            <div className=" sm:flex sm:flex-row-reverse ">
                                              <div className="mt-4">
                                                <button
                                                  type="button"
                                                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                  onClick={() => {
                                                    setshowEmailerror(false);
                                                    setCustomEmail('');
                                                    setShowCustomEmailDialog(false);
                                                  }}
                                                >
                                                  Cancel
                                                </button>
                                                <button
                                                  type="button"
                                                  className={classNames(
                                                    button.primary({ isDisabled: false }),
                                                    'ml-2 w-auto inline-flex items-center justify-center rounded-md text-sm',
                                                  )}
                                                  onClick={() => handleCustomUserEmailSubmit(customEmail)}
                                                >
                                                  Add
                                                </button>
                                              </div>
                                            </div>
                                          </Dialog.Panel>
                                        </Transition.Child>
                                      </div>
                                    </div>
                                  </Dialog>
                                </Transition>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {isEmailSending && (
                      <div className="fixed bottom-0 left-0 right-0 h-1 bg-blue-600 overflow-hidden">
                        <div className="h-full bg-blue-400 animate-pulse"></div>
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    {exportMethod === ExportMethod.Email ? (
                      <button
                        type="button"
                        className={classNames(
                          emailSent ? button.primary({ isDisabled: false }) : button.primary({ isDisabled: false }),
                          'w-auto inline-flex items-center justify-center rounded-md text-sm ml-2',
                        )}
                        disabled={isEmailSending}
                        onClick={handleExport}
                      >
                        {isEmailSending ? (
                          <>
                            <span className="mr-2 font-semibold">Sending</span>
                            <Loader color="white" />
                          </>
                        ) : emailSent ? (
                          <>
                            <span className="font-semibold">Sent</span>
                            <CheckIcon strokeWidth={15} className="w-4 h-4 ml-2" />
                          </>
                        ) : (
                          <span className="font-semibold">Send Email</span>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={classNames(
                          button.primary({ isDisabled: false }),
                          'w-auto inline-flex items-center justify-center rounded-md text-sm ml-2',
                        )}
                        disabled={preparingPrint}
                        onClick={fetchDataAndPrint}
                      >
                        {preparingPrint ? (
                          <>
                            <span className="mr-2 font-semibold">Preparing</span>
                            <Loader color="white" />
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">Print</span>
                            <div style={{ display: 'none' }}>
                              {type === ExportTypes.Actions && (
                                <PrintComponent ref={printRef} data={data} type={ExportTypes.Actions} />
                              )}
                              {type === ExportTypes.Objectives && (
                                <PrintComponent ref={printRef} data={data} type={ExportTypes.Objectives} />
                              )}
                            </div>
                          </>
                        )}
                      </button>
                    )}

                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                  <ErrorToast error={error} setError={setError} />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ExportPopup;
